/**
 * UserManagementPage.jsx  —  Admin · User Management  (v2)
 *
 * Design system: matches AdminHome/AdminLayout
 *   #0c1118 dark · #f9ab14 amber · Bebas Neue + DM Sans + DM Mono
 *
 * KPI cards filter the table when clicked.
 * Table uses inline styles (no Tailwind dependency conflicts).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  Filter,
  Users,
  Shield,
  UserX,
  UserCheck,
  Mail,
  RefreshCw,
  ChevronDown,
  Rows,
  BadgeCheck,
  Settings,
  Check,
  X,
  Info,
  Trash2,
  Crown,
  Briefcase,
  Users as UsersIcon,
  User as UserIcon,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Clock,
} from "lucide-react";
import {
  admin,
  adminGetManagerPerms,
  adminSaveManagerPerms,
  getUserDetail,
  deleteUser,
  listOwners,
  listOrgsByOwner,
  deleteOwnerWithTransfer,
} from "../../services/adminApi";
import { useToast } from "../../components/ToastProvider";
import ManagerPermsDialog from "../../components/ManagerPermsDialog";
import DeleteOwnerWizard from "../../components/DeleteOwnerWizard";

/* ─── tokens (mirrors AdminHome) ─────────────────────────────────── */
const T = {
  fontDisplay: "'DM Sans', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  fontMono: "'DM Sans', sans-serif",
  amber: "#111111",
  amberDark: "#333333",
  amberBg: "rgba(0,0,0,0.06)",
  amberBorder: "rgba(0,0,0,0.12)",
  dark: "#0c1118",
  white: "#ffffff",
  bg: "#f4f6f8",
  surface: "#ffffff",
  border: "#e8ecf0",
  muted: "#6b7280",
  mutedLight: "#9ca3af",
  green: "#22c55e",
  red: "#ef4444",
  indigo: "#6366f1",
  purple: "#8b5cf6",
};

const DEFAULT_CEED_PERMS = [
  "manage_purchase_plans",
  "view_quotes",
  "send_engineered_plans",
  "view_users",
];

/* ─── inject CSS once ───────────────────────────────────────────── */
(function boot() {
  if (typeof document === "undefined") return;
  if (!document.getElementById("ump-fonts")) {
    const l = document.createElement("link");
    l.id = "ump-fonts";
    l.rel = "stylesheet";
    l.href =
      "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
    document.head.appendChild(l);
  }
  if (!document.getElementById("ump-css")) {
    const s = document.createElement("style");
    s.id = "ump-css";
    s.textContent = `
      @keyframes ump-fade  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      @keyframes ump-spin  { to{transform:rotate(360deg)} }
      @keyframes ump-trial-warn {
        0%,100% { opacity:1; }
        50%      { opacity:.6; }
      }
      @keyframes ump-shimmer {
        0%{background-position:-600px 0} 100%{background-position:600px 0}
      }

      .ump-page { font-family:'DM Sans',sans-serif; background:#f4f6f8; min-height:100vh; padding:0; animation:ump-fade .25s ease; box-sizing:border-box; width:100%; overflow-x:hidden; }

      /* KPI cards */
      .ump-kpi {
        display:flex; align-items:stretch; cursor:pointer; overflow:hidden;
        border:1px solid #e8ecf0; text-align:left; width:100%; padding:0;
        background:#fff; border-radius:14px;
        transition:box-shadow .18s,transform .18s,border-color .18s;
        position:relative; min-width:0; box-sizing:border-box;
      }
      .ump-kpi:hover {
        box-shadow:0 8px 32px rgba(0,0,0,.09);
        transform:translateY(-2px);
      }
      .ump-kpi.active {
        border-color:#111111;
        box-shadow:0 0 0 3px rgba(0,0,0,0.10);
      }
      .ump-kpi-accent { width:4px; flex-shrink:0; border-radius:14px 0 0 14px; }
      .ump-kpi-body {
        flex:1; padding:18px 20px;
        display:flex; align-items:center; justify-content:space-between; gap:12px;
      }
      .ump-kpi-label { font-size:.68rem; letter-spacing:.04em; color:#6b7280; font-family:'DM Sans',sans-serif; }
      .ump-kpi-value { font-size:1.85rem; font-weight:700; letter-spacing:.02em; color:#0c1118; line-height:1; font-family:'DM Sans',sans-serif; }
      .ump-kpi-sub { font-size:.62rem; letter-spacing:.06em; font-family:'DM Sans',sans-serif; margin-top:2px; }

      /* Table */
      .ump-table-wrap {
        background:#fff; border:1px solid #e8ecf0; border-radius:14px;
        overflow:hidden; position:relative; width:100%; box-sizing:border-box;
      }
      .ump-th {
        padding:11px 16px; text-align:left;
        font-family:'DM Sans',sans-serif; font-size:.57rem; letter-spacing:.04em;
        color:#6b7280; font-weight:600;
        background:#f9fafb; border-bottom:1px solid #e8ecf0;
        white-space:nowrap; user-select:none;
      }
      .ump-th button {
        display:inline-flex; align-items:center; gap:5px;
        background:none; border:none; cursor:pointer; padding:0;
        font-family:inherit; font-size:inherit; letter-spacing:inherit;
        text-transform:inherit; color:inherit; font-weight:inherit;
      }
      .ump-th button:hover { color:#0c1118; }
      .ump-td { padding:13px 16px; vertical-align:middle; border-bottom:1px solid #f3f4f6; }
      .ump-tr { transition:background .12s; }
      .ump-tr:hover { background:#fafafa; }
      .ump-tr:last-child .ump-td { border-bottom:none; }
      .ump-tr.selected { background:rgba(0,0,0,0.03); }

      /* Badges */
      .ump-pill {
        display:inline-flex; align-items:center; gap:4px;
        padding:3px 9px; border-radius:20px;
        font-family:'DM Sans',sans-serif; font-size:.6rem;
        letter-spacing:.07em; font-weight:700;
        white-space:nowrap;
      }
      .ump-pill-dot {
        width:5px; height:5px; border-radius:50%; flex-shrink:0;
      }

      /* Action icon buttons */
      .ump-icon-btn {
        width:32px; height:32px; border-radius:8px; border:none; cursor:pointer;
        display:inline-flex; align-items:center; justify-content:center;
        background:transparent; transition:background .12s, color .12s;
        position:relative;
      }
      .ump-icon-btn:disabled { opacity:.4; cursor:not-allowed; }
      .ump-icon-btn:hover:not(:disabled) { background:#f3f4f6; }
      .ump-icon-btn.ired:hover:not(:disabled)    { background:#fee2e2; color:#dc2626; }
      .ump-icon-btn.igreen:hover:not(:disabled)  { background:#dcfce7; color:#16a34a; }
      .ump-icon-btn.ipurple:hover:not(:disabled) { background:#ede9fe; color:#7c3aed; }
      .ump-icon-btn.iamber:hover:not(:disabled)  { background:rgba(0,0,0,0.06); color:#333333; }
      .ump-icon-btn.iblue:hover:not(:disabled)   { background:#dbeafe; color:#1d4ed8; }

      /* Tooltip */
      .ump-tip {
        position:absolute; bottom:calc(100% + 6px); left:50%; transform:translateX(-50%);
        background:#0c1118; color:#e5e7eb; font-family:'DM Sans',sans-serif;
        font-size:.6rem; letter-spacing:.06em; padding:5px 10px;
        border-radius:6px; white-space:nowrap; pointer-events:none; z-index:80;
      }
      .ump-tip::after {
        content:''; position:absolute; top:100%; left:50%; transform:translateX(-50%);
        border:4px solid transparent; border-top-color:#0c1118;
      }

      /* Search/filter bar */
      .ump-input {
        font-family:'DM Sans',sans-serif; font-size:.875rem;
        border:1px solid #e8ecf0; border-radius:9px; background:#fff;
        transition:border-color .15s, box-shadow .15s; outline:none;
      }
      .ump-input:focus { border-color:#111111; box-shadow:0 0 0 3px rgba(0,0,0,0.08); }

      /* Pagination */
      .ump-page-btn {
        width:36px; height:36px; border-radius:8px; border:1px solid #e8ecf0;
        background:#fff; cursor:pointer; font-family:'DM Sans',sans-serif;
        font-size:.78rem; font-weight:600; color:#374151;
        display:inline-flex; align-items:center; justify-content:center;
        transition:all .15s;
      }
      .ump-page-btn:hover:not(:disabled) { border-color:#111111; color:#0c1118; }
      .ump-page-btn.pactive { background:#0c1118; color:#fff; border-color:#0c1118; }
      .ump-page-btn:disabled { opacity:.4; cursor:not-allowed; }

      /* Shimmer */
      .ump-shimmer {
        background:linear-gradient(90deg,#f0f2f5 25%,#e4e6ea 50%,#f0f2f5 75%);
        background-size:600px 100%; animation:ump-shimmer 1.4s ease infinite;
        border-radius:6px;
      }

      /* Spinner overlay */
      .ump-overlay {
        position:absolute; inset:0; background:rgba(255,255,255,.85);
        display:flex; align-items:center; justify-content:center;
        z-index:10; border-radius:14px;
      }

      /* Drawer */
      .ump-drawer {
        position:fixed; top:0; right:0; bottom:0; width:min(520px,96vw);
        background:#fff; z-index:61; display:flex; flex-direction:column;
        box-shadow:-4px 0 48px rgba(0,0,0,.18);
        transition:transform .28s cubic-bezier(.4,0,.2,1);
      }
      .ump-drawer.open   { transform:translateX(0); }
      .ump-drawer.closed { transform:translateX(100%); }

      /* Responsive KPI grid */
      .ump-kpi-grid {
        display:grid; grid-template-columns:repeat(4,1fr); gap:14px; width:100%; box-sizing:border-box;
      }
      @media(max-width:1100px) { .ump-kpi-grid { grid-template-columns:repeat(2,1fr); } }
      @media(max-width:600px)  { .ump-kpi-grid { grid-template-columns:1fr; } }

      /* Avatar */
      .ump-avatar {
        width:36px; height:36px; border-radius:50%; flex-shrink:0;
        display:flex; align-items:center; justify-content:center;
        font-family:'DM Sans',sans-serif; font-size:1.1rem; color:#fff;
        position:relative;
      }
      .ump-avatar-dot {
        position:absolute; bottom:-1px; right:-1px;
        width:10px; height:10px; border-radius:50%; border:2px solid #fff;
      }

      /* Checkbox */
      .ump-cb { width:15px; height:15px; accent-color:#111111; cursor:pointer; }

      /* ── Mobile: hide secondary table columns ─── */
      .ump-col-secondary { }

      /* ── Responsive breakpoints ─────────────────── */
      @media(max-width:900px) {
        .ump-kpi-body { padding:14px 16px; }
        .ump-kpi-value { font-size:1.5rem; }
        .ump-drawer { width:100vw; max-width:100vw; }
      }

      @media(max-width:768px) {
        /* Drawer full width */
        .ump-drawer { width:100vw; max-width:100vw; }

        /* Pagination stacks */
        .ump-pager-inner { flex-direction:column; align-items:stretch !important; gap:10px !important; }
        .ump-pager-pages { justify-content:center; }
      }

      @media(max-width:640px) {
        /* Hide secondary columns on small screens */
        .ump-col-secondary { display:none; }

        /* Tighter table cells */
        .ump-th { padding:9px 10px; font-size:.5rem; }
        .ump-td { padding:10px 10px; }

        /* Shrink avatar */
        .ump-avatar { width:30px; height:30px; font-size:.9rem; }

        /* Tighter icon buttons */
        .ump-icon-btn { width:28px; height:28px; }

        /* KPI adjustments */
        .ump-kpi-value { font-size:1.3rem; }
        .ump-kpi-body { padding:12px 14px; }
      }

      @media(max-width:400px) {
        .ump-th { padding:8px 8px; }
        .ump-td { padding:9px 8px; }
      }
    `;
    document.head.appendChild(s);
  }
})();

/* ─── hook ──────────────────────────────────────────────────────── */
function useIsMobile(bp = 768) {
  const get = () => typeof window !== "undefined" && window.innerWidth <= bp;
  const [v, setV] = useState(get);
  useEffect(() => {
    const fn = () => setV(get());
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [bp]); // eslint-disable-line
  return v;
}

/* ═══════════════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════════════ */
export default function UserManagementPage() {
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sortField, setSortField] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [drawerUserId, setDrawerUserId] = useState(null);
  const [ownerToDelete, setOwnerToDelete] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [activeKpi, setActiveKpi] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await admin.users({
        q,
        role,
        status,
        page,
        pageSize,
        sortField,
        sortDirection: sortDir,
      });
      setRows(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [q, role, status, page, pageSize, sortField, sortDir]); // eslint-disable-line

  /* KPI card click → apply filter */
  function handleKpiClick(key) {
    if (activeKpi === key) {
      setActiveKpi(null);
      setRole("all");
      setStatus("all");
      setPage(1);
    } else {
      setActiveKpi(key);
      setPage(1);
      if (key === "total") {
        setRole("all");
        setStatus("all");
      }
      if (key === "active") {
        setRole("all");
        setStatus("active");
      }
      if (key === "suspended") {
        setRole("all");
        setStatus("suspended");
      }
      if (key === "admins") {
        setRole("admin");
        setStatus("all");
      }
    }
  }

  /* Sync activeKpi highlight when filters changed manually */
  useEffect(() => {
    if (role === "admin" && status === "all") setActiveKpi("admins");
    else if (status === "active" && role === "all") setActiveKpi("active");
    else if (status === "suspended" && role === "all")
      setActiveKpi("suspended");
    else if (role === "all" && status === "all") setActiveKpi(null);
  }, [role, status]);

  function handleSelect(id, checked) {
    setSelectedUsers((p) => (checked ? [...p, id] : p.filter((x) => x !== id)));
  }
  function handleSelectAll(checked) {
    setSelectedUsers(checked ? rows.map((u) => u.id) : []);
  }
  async function handleBulk(action) {
    if (!selectedUsers.length) return;
    try {
      setLoading(true);
      await admin.bulkUpdateUsers(selectedUsers, action);
      setSelectedUsers([]);
      load();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  const stats = {
    total,
    active: rows.filter((u) => u.status === "active").length,
    suspended: rows.filter((u) => u.status === "suspended").length,
    admins: rows.filter((u) => u.role === "admin").length,
  };

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(s) ||
        (u.email || "").toLowerCase().includes(s),
    );
  }, [rows, q]);

  return (
    <div className="ump-page">
      <div
        style={{
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 22,
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* ── Header ────────────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 24,
                fontWeight: 700,
                color: "#0c1118",
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              User Management
            </h1>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                color: "#6b7280",
                margin: "4px 0 0",
              }}
            >
              Manage accounts, roles, and permissions across the platform.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              padding: "9px 18px",
              background: loading ? "#f3f4f6" : T.dark,
              color: loading ? T.mutedLight : T.white,
              border: "none",
              borderRadius: 9,
              fontFamily: T.fontBody,
              fontSize: ".82rem",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            <RefreshCw
              size={13}
              style={{
                animation: loading ? "ump-spin .8s linear infinite" : "none",
              }}
            />
            Refresh
          </button>
        </div>

        {/* ── KPI cards ─────────────────────────────────────────────── */}
        <div className="ump-kpi-grid">
          <KpiCard
            label="Total Users"
            value={stats.total}
            accent={T.dark}
            icon={<Users size={17} />}
            active={activeKpi === "total"}
            onClick={() => handleKpiClick("total")}
            sub="All registered accounts"
          />
          <KpiCard
            label="Active"
            value={stats.active}
            accent={T.green}
            icon={<UserCheck size={17} />}
            active={activeKpi === "active"}
            onClick={() => handleKpiClick("active")}
            sub="Currently active users"
          />
          <KpiCard
            label="Suspended"
            value={stats.suspended}
            accent={T.red}
            icon={<UserX size={17} />}
            active={activeKpi === "suspended"}
            onClick={() => handleKpiClick("suspended")}
            sub="Access restricted"
          />
          <KpiCard
            label="Administrators"
            value={stats.admins}
            accent={T.purple}
            icon={<Shield size={17} />}
            active={activeKpi === "admins"}
            onClick={() => handleKpiClick("admins")}
            sub="Platform admins"
          />
        </div>

        {/* ── Filter bar ────────────────────────────────────────────── */}
        <div
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            padding: 16,
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {/* Search */}
            <div style={{ flex: 1, minWidth: 0, width: "100%", position: "relative" }}>
              <Search
                size={14}
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: T.mutedLight,
                }}
              />
              <input
                className="ump-input"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  paddingLeft: 36,
                  paddingRight: 14,
                  paddingTop: 10,
                  paddingBottom: 10,
                }}
                placeholder="Search by name or email…"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            {/* Role filter */}
            <div style={{ position: "relative", flex: 1, minWidth: 120 }}>
              <Filter
                size={13}
                style={{
                  position: "absolute",
                  left: 11,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: T.mutedLight,
                }}
              />
              <select
                className="ump-input"
                style={{
                  paddingLeft: 30,
                  paddingRight: 32,
                  paddingTop: 10,
                  paddingBottom: 10,
                  appearance: "none",
                  minWidth: 0,
                  width: "100%",
                  boxSizing: "border-box",
                  cursor: "pointer",
                }}
                value={role}
                onChange={(e) => {
                  setRole(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="manager">Ceed Manager</option>
                <option value="user">User</option>
              </select>
              <ChevronDown
                size={13}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: T.mutedLight,
                  pointerEvents: "none",
                }}
              />
            </div>

            {/* Status filter */}
            <div style={{ position: "relative", flex: 1, minWidth: 120 }}>
              <select
                className="ump-input"
                style={{
                  paddingLeft: 14,
                  paddingRight: 32,
                  paddingTop: 10,
                  paddingBottom: 10,
                  appearance: "none",
                  minWidth: 0,
                  width: "100%",
                  boxSizing: "border-box",
                  cursor: "pointer",
                }}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
              <ChevronDown
                size={13}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: T.mutedLight,
                  pointerEvents: "none",
                }}
              />
            </div>

            {/* Active filter chip */}
            {activeKpi && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  background: T.amberBg,
                  border: `1px solid ${T.amberBorder}`,
                  borderRadius: 20,
                }}
              >
                <span
                  style={{
                    fontFamily: T.fontBody,
                    fontSize: ".6rem",
                    letterSpacing: ".04em",
                    color: T.amberDark,
                    fontWeight: 700,
                  }}
                >
                  Filtered: {activeKpi}
                </span>
                <button
                  onClick={() => handleKpiClick(activeKpi)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: T.amberDark,
                    display: "flex",
                    padding: 0,
                    marginTop: 1,
                  }}
                >
                  <X size={11} />
                </button>
              </div>
            )}
          </div>

          {/* Bulk action bar */}
          {selectedUsers.length > 0 && (
            <div
              style={{
                marginTop: 14,
                padding: "12px 16px",
                background: "rgba(99,102,241,.05)",
                border: "1px solid rgba(99,102,241,.18)",
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <span
                style={{
                  fontFamily: T.fontBody,
                  fontSize: ".62rem",
                  letterSpacing: ".04em",
                  color: T.indigo,
                  fontWeight: 700,
                }}
              >
                {selectedUsers.length} user{selectedUsers.length > 1 ? "s" : ""}{" "}
                selected
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <ActionBtn onClick={() => handleBulk("activate")} bg="#16a34a">
                  Activate
                </ActionBtn>
                <ActionBtn onClick={() => handleBulk("suspend")} bg={T.red}>
                  Suspend
                </ActionBtn>
                <ActionBtn onClick={() => setSelectedUsers([])} ghost>
                  Clear
                </ActionBtn>
              </div>
            </div>
          )}
        </div>

        {/* ── Table (desktop) / Cards (mobile) ──────────────────────── */}
        {isMobile ? (
          /* ── Mobile card list ─────────────────────────────────────── */
          <div style={{ display: "flex", flexDirection: "column", gap: 10, position: "relative" }}>
            {loading && (
              <div className="ump-overlay" style={{ borderRadius: 14 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                  <RefreshCw size={22} style={{ color: T.amber, animation: "ump-spin .8s linear infinite" }} />
                  <span style={{ fontFamily: T.fontBody, fontSize: ".65rem", letterSpacing: ".04em", color: T.muted }}>Loading…</span>
                </div>
              </div>
            )}
            {filtered.length === 0 && !loading ? (
              <div style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 14, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", gap: 12 }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", color: T.mutedLight }}><Users size={24} /></div>
                <span style={{ fontFamily: T.fontBody, fontWeight: 600, fontSize: ".9rem", color: "#374151" }}>No users found</span>
                <span style={{ fontFamily: T.fontBody, fontSize: ".8rem", color: T.mutedLight }}>Try adjusting your search or filters</span>
              </div>
            ) : (
              filtered.map((user) => (
                <UserCard
                  key={user.id}
                  user={user}
                  isSelected={selectedUsers.includes(user.id)}
                  onSelect={handleSelect}
                  onUpdate={load}
                  onShowDetails={(id) => setDrawerUserId(id)}
                  onOpenOwnerDelete={(id) => { setOwnerToDelete(id); setShowWizard(true); }}
                />
              ))
            )}
          </div>
        ) : (
          /* ── Desktop table ────────────────────────────────────────── */
          <div className="ump-table-wrap">
            {loading && (
              <div className="ump-overlay">
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                  <RefreshCw size={22} style={{ color: T.amber, animation: "ump-spin .8s linear infinite" }} />
                  <span style={{ fontFamily: T.fontBody, fontSize: ".65rem", letterSpacing: ".04em", color: T.muted }}>Loading…</span>
                </div>
              </div>
            )}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th className="ump-th" style={{ width: 44 }}>
                      <input type="checkbox" className="ump-cb" checked={selectedUsers.length === rows.length && rows.length > 0} onChange={(e) => handleSelectAll(e.target.checked)} />
                    </th>
                    <SortTh label="User" field="name" sortField={sortField} sortDir={sortDir} onSort={(f, d) => { setSortField(f); setSortDir(d); }} />
                    <th className="ump-th">Role</th>
                    <th className="ump-th ump-col-secondary">Account Type</th>
                    <th className="ump-th ump-col-secondary">Plan</th>
                    <th className="ump-th ump-col-secondary">Verified</th>
                    <th className="ump-th">Status</th>
                    <th className="ump-th" style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && !loading ? (
                    <tr>
                      <td colSpan={8}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "64px 24px", gap: 12 }}>
                          <div style={{ width: 56, height: 56, borderRadius: 14, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", color: T.mutedLight }}><Users size={24} /></div>
                          <span style={{ fontFamily: T.fontBody, fontWeight: 600, fontSize: ".9rem", color: "#374151" }}>No users found</span>
                          <span style={{ fontFamily: T.fontBody, fontSize: ".8rem", color: T.mutedLight }}>Try adjusting your search or filters</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((user) => (
                      <UserRow
                        key={user.id}
                        user={user}
                        isSelected={selectedUsers.includes(user.id)}
                        onSelect={handleSelect}
                        onUpdate={load}
                        onShowDetails={(id) => setDrawerUserId(id)}
                        onOpenOwnerDelete={(id) => { setOwnerToDelete(id); setShowWizard(true); }}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Pagination ────────────────────────────────────────────── */}
        <Pager
          page={page}
          total={total}
          pageSize={pageSize}
          loading={loading}
          onPage={setPage}
          onPageSize={(sz) => {
            setPageSize(sz);
            setPage(1);
          }}
        />
      </div>

      {/* ── Drawers / wizards ─────────────────────────────────────── */}
      {drawerUserId && (
        <UserDetailDrawer
          userId={drawerUserId}
          onClose={() => setDrawerUserId(null)}
          onDeleted={() => {
            setDrawerUserId(null);
            load();
          }}
        />
      )}
      <DeleteOwnerWizard
        ownerId={ownerToDelete || ""}
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onDone={() => {
          setShowWizard(false);
          setOwnerToDelete(null);
          load();
        }}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   KPI CARD
═══════════════════════════════════════════════════════════════════ */
function KpiCard({ label, value, accent, icon, active, onClick, sub }) {
  return (
    <button
      className={`ump-kpi${active ? " active" : ""}`}
      onClick={onClick}
      title={active ? "Click to clear filter" : `Filter by ${label}`}
    >
      <div className="ump-kpi-accent" style={{ background: accent }} />
      <div className="ump-kpi-body">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            minWidth: 0,
          }}
        >
          <span className="ump-kpi-label">{label}</span>
          <span className="ump-kpi-value">{value}</span>
          <span
            className="ump-kpi-sub"
            style={{ color: active ? T.amberDark : T.mutedLight }}
          >
            {active ? "▸ Click to clear filter" : sub}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 6,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              background: `${accent}15`,
              border: `1px solid ${accent}28`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: accent,
            }}
          >
            {icon}
          </div>
          {active && (
            <span
              style={{
                fontFamily: T.fontBody,
                fontSize: ".55rem",
                letterSpacing: ".04em",
                color: T.amberDark,
                background: T.amberBg,
                border: `1px solid ${T.amberBorder}`,
                borderRadius: 20,
                padding: "2px 8px",
                fontWeight: 700,
              }}
            >
              ON
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

/* ── Sortable TH ────────────────────────────────────────────────── */
function SortTh({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  const next = active && sortDir === "asc" ? "desc" : "asc";
  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className="ump-th">
      <button onClick={() => onSort(field, next)}>
        {label}
        <Icon size={11} style={{ color: active ? T.amber : T.mutedLight }} />
      </button>
    </th>
  );
}

/* ── Small action button ────────────────────────────────────────── */
function ActionBtn({ children, onClick, bg, ghost }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 14px",
        borderRadius: 7,
        fontFamily: T.fontBody,
        fontSize: ".78rem",
        fontWeight: 700,
        cursor: "pointer",
        border: ghost ? `1px solid ${T.border}` : "none",
        background: ghost ? "#fff" : bg,
        color: ghost ? T.muted : "#fff",
        transition: "opacity .12s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = ".8")}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
    >
      {children}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   USER ROW
═══════════════════════════════════════════════════════════════════ */
function UserRow({
  user,
  isSelected,
  onSelect,
  onUpdate,
  onShowDetails,
  onOpenOwnerDelete,
}) {
  const [loading, setLoading] = useState(false);
  const [showMakeAdmin, setShowMakeAdmin] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [permsOpen, setPermsOpen] = useState(false);
  const [permsUser, setPermsUser] = useState(null);
  const [permsData, setPermsData] = useState({ allow: [], deny: [] });
  const toast = useToast();

  const isOwner =
    user.companyBadge?.type === "owner" ||
    user.ownedOrgCount > 0 ||
    user.accountType === "owner";

  async function openPerms() {
    try {
      const d = await adminGetManagerPerms(user.id);
      setPermsUser(user);
      setPermsData(d);
      setPermsOpen(true);
    } catch {
      toast.error("Failed to load permissions");
    }
  }
  async function savePerms(payload) {
    try {
      await adminSaveManagerPerms(permsUser.id, payload);
      setPermsOpen(false);
      toast.success("Permissions updated");
    } catch {
      toast.error("Failed to save");
    }
  }
  async function handleRoleToggle() {
    if (user.role === "admin") {
      setLoading(true);
      try {
        await admin.setUser(user.id, { action: "remove_admin" });
        onUpdate();
      } finally {
        setLoading(false);
      }
      return;
    }
    setShowMakeAdmin(true);
  }
  async function confirmAdmin() {
    setLoading(true);
    try {
      await admin.setUser(user.id, { action: "make_admin" });
      onUpdate();
      setShowMakeAdmin(false);
    } catch {
      toast.error("Failed to make admin");
    } finally {
      setLoading(false);
    }
  }
  async function handleStatusToggle() {
    setLoading(true);
    try {
      await admin.setUser(user.id, {
        action: user.status === "active" ? "suspend" : "activate",
      });
      onUpdate();
    } finally {
      setLoading(false);
    }
  }
  async function handleToggleCeed() {
    setLoading(true);
    try {
      if (user.role === "manager")
        await admin.patchUser(user.id, { role: "user" });
      else
        await admin.patchUser(user.id, {
          role: "manager",
          ceedPermissions: DEFAULT_CEED_PERMS,
        });
      onUpdate();
    } catch {
      toast.error("Failed to update Ceed role");
    } finally {
      setLoading(false);
    }
  }
  async function handleDelete() {
    try {
      setConfirmDel(false);
      await deleteUser(user.id);
      toast.success("User deleted");
      onUpdate();
    } catch (e) {
      if (e?.code === "owner_conflict") {
        onOpenOwnerDelete?.(user.id);
        return;
      }
      toast.error(e?.message || "Delete failed");
    }
  }

  /* avatar gradient based on email hash */
  const hue = useMemo(() => {
    let h = 0;
    for (const c of user.email || "x") h = (h * 31 + c.charCodeAt(0)) & 0xffff;
    return h % 360;
  }, [user.email]);

  return (
    <tr className={`ump-tr${isSelected ? " selected" : ""}`}>
      {/* Checkbox */}
      <td className="ump-td" style={{ width: 44 }}>
        <input
          type="checkbox"
          className="ump-cb"
          checked={isSelected}
          onChange={(e) => onSelect(user.id, e.target.checked)}
        />
      </td>

      {/* User */}
      <td className="ump-td" style={{ minWidth: 220 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div
            className="ump-avatar"
            style={{
              background: `linear-gradient(135deg, hsl(${hue},60%,45%), hsl(${(hue + 40) % 360},70%,55%))`,
            }}
          >
            {(user.name || "U")[0].toUpperCase()}
            <div
              className="ump-avatar-dot"
              style={{ background: user.status === "active" ? T.green : T.red }}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontFamily: T.fontBody,
                fontWeight: 600,
                fontSize: ".875rem",
                color: "#111827",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 200,
              }}
            >
              {user.name || "Unnamed"}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                marginTop: 2,
              }}
            >
              <Mail size={11} style={{ color: T.mutedLight, flexShrink: 0 }} />
              <span
                style={{
                  fontFamily: T.fontBody,
                  fontSize: ".65rem",
                  color: T.muted,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 180,
                }}
              >
                {user.email}
              </span>
            </div>
          </div>
        </div>
      </td>

      {/* Role */}
      <td className="ump-td">
        <RolePill role={user.role} />
      </td>

      {/* Account type */}
      <td className="ump-td ump-col-secondary">
        {user.companyBadge ? (
          <AccountTypePill
            type={user.companyBadge.type}
            orgName={user.companyBadge.orgName}
          />
        ) : (
          <AccountTypePill type="individual" />
        )}
      </td>

      {/* Plan */}
      <td className="ump-td ump-col-secondary">
        <PlanPill plan={user.plan} trialEndsAt={user.trialEndsAt} />
      </td>

      {/* Verified */}
      <td className="ump-td ump-col-secondary">
        <VerifiedPill verified={user.verified} />
      </td>

      {/* Status */}
      <td className="ump-td">
        <StatusPill status={user.status} />
      </td>

      {/* Actions */}
      <td className="ump-td" style={{ textAlign: "right" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 3,
          }}
        >
          {user.role === "manager" && (
            <IconBtn
              icon={Settings}
              tooltip="Permissions"
              onClick={openPerms}
              disabled={loading}
              variant="iamber"
            />
          )}
          <IconBtn
            icon={BadgeCheck}
            tooltip={
              user.role === "manager"
                ? "Remove Ceed Manager"
                : "Make Ceed Manager"
            }
            onClick={handleToggleCeed}
            disabled={loading}
            variant={user.role === "manager" ? "iblue" : "default"}
          />
          <IconBtn
            icon={Shield}
            tooltip={user.role === "admin" ? "Remove Admin" : "Make Admin"}
            onClick={handleRoleToggle}
            disabled={loading}
            variant={user.role === "admin" ? "ipurple" : "default"}
          />
          <IconBtn
            icon={user.status === "active" ? UserX : UserCheck}
            tooltip={user.status === "active" ? "Suspend" : "Activate"}
            onClick={handleStatusToggle}
            disabled={loading}
            variant={user.status === "active" ? "ired" : "igreen"}
          />
          <IconBtn
            icon={Info}
            tooltip="View details"
            onClick={() => onShowDetails(user.id)}
            disabled={loading}
            variant="default"
          />
          <IconBtn
            icon={Trash2}
            tooltip={isOwner ? "Delete (transfer)" : "Delete user"}
            onClick={() =>
              isOwner ? onOpenOwnerDelete(user.id) : setConfirmDel(true)
            }
            disabled={loading}
            variant="ired"
          />
        </div>

        <ManagerPermsDialog
          open={permsOpen}
          onClose={() => setPermsOpen(false)}
          user={permsUser}
          initial={permsData}
          onSave={savePerms}
        />
      </td>

      <MakeAdminDialog
        open={showMakeAdmin}
        onClose={() => setShowMakeAdmin(false)}
        onConfirm={confirmAdmin}
      />

      {confirmDel && (
        <ConfirmDialog
          title="Delete User"
          body={
            <>
              Email: <strong>{user.email}</strong>
              <br />
              This action is permanent and cannot be undone.
            </>
          }
          confirmLabel="Delete"
          danger
          onCancel={() => setConfirmDel(false)}
          onConfirm={handleDelete}
        />
      )}
    </tr>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   USER CARD  (mobile)
═══════════════════════════════════════════════════════════════════ */
function UserCard({ user, isSelected, onSelect, onUpdate, onShowDetails, onOpenOwnerDelete }) {
  const [loading, setLoading] = useState(false);
  const [showMakeAdmin, setShowMakeAdmin] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [permsOpen, setPermsOpen] = useState(false);
  const [permsUser, setPermsUser] = useState(null);
  const [permsData, setPermsData] = useState({ allow: [], deny: [] });
  const toast = useToast();

  const isOwner = user.companyBadge?.type === "owner" || user.ownedOrgCount > 0 || user.accountType === "owner";

  const hue = useMemo(() => {
    let h = 0;
    for (const c of user.email || "x") h = (h * 31 + c.charCodeAt(0)) & 0xffff;
    return h % 360;
  }, [user.email]);

  async function openPerms() {
    try {
      const d = await adminGetManagerPerms(user.id);
      setPermsUser(user); setPermsData(d); setPermsOpen(true);
    } catch { toast.error("Failed to load permissions"); }
  }
  async function savePerms(payload) {
    try {
      await adminSaveManagerPerms(permsUser.id, payload);
      setPermsOpen(false); toast.success("Permissions updated");
    } catch { toast.error("Failed to save"); }
  }
  async function handleRoleToggle() {
    if (user.role === "admin") {
      setLoading(true);
      try { await admin.setUser(user.id, { action: "remove_admin" }); onUpdate(); }
      finally { setLoading(false); }
      return;
    }
    setShowMakeAdmin(true);
  }
  async function confirmAdmin() {
    setLoading(true);
    try { await admin.setUser(user.id, { action: "make_admin" }); onUpdate(); setShowMakeAdmin(false); }
    catch { toast.error("Failed to make admin"); }
    finally { setLoading(false); }
  }
  async function handleStatusToggle() {
    setLoading(true);
    try { await admin.setUser(user.id, { action: user.status === "active" ? "suspend" : "activate" }); onUpdate(); }
    finally { setLoading(false); }
  }
  async function handleToggleCeed() {
    setLoading(true);
    try {
      if (user.role === "manager") await admin.patchUser(user.id, { role: "user" });
      else await admin.patchUser(user.id, { role: "manager", ceedPermissions: DEFAULT_CEED_PERMS });
      onUpdate();
    } catch { toast.error("Failed to update Ceed role"); }
    finally { setLoading(false); }
  }
  async function handleDelete() {
    try {
      setConfirmDel(false);
      await deleteUser(user.id);
      toast.success("User deleted"); onUpdate();
    } catch (e) {
      if (e?.code === "owner_conflict") { onOpenOwnerDelete?.(user.id); return; }
      toast.error(e?.message || "Delete failed");
    }
  }

  return (
    <>
      <div style={{
        background: isSelected ? "rgba(0,0,0,0.03)" : "#fff",
        border: `1px solid ${isSelected ? T.amber : T.border}`,
        borderRadius: 12,
        overflow: "hidden",
        opacity: loading ? 0.7 : 1,
        transition: "opacity .15s",
      }}>
        {/* Top: avatar + info + pills */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 14px 12px" }}>
          {/* Checkbox */}
          <input type="checkbox" className="ump-cb" style={{ marginTop: 3, flexShrink: 0 }}
            checked={isSelected} onChange={(e) => onSelect(user.id, e.target.checked)} />

          {/* Avatar */}
          <div className="ump-avatar" style={{ background: `linear-gradient(135deg, hsl(${hue},60%,45%), hsl(${(hue+40)%360},70%,55%))`, flexShrink: 0 }}>
            {(user.name || "U")[0].toUpperCase()}
            <div className="ump-avatar-dot" style={{ background: user.status === "active" ? T.green : T.red }} />
          </div>

          {/* Name / email / pills */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: T.fontBody, fontWeight: 700, fontSize: ".9rem", color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {user.name || "Unnamed"}
            </div>
            <div style={{ fontFamily: T.fontBody, fontSize: ".62rem", color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 }}>
              {user.email}
            </div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 7 }}>
              <RolePill role={user.role} />
              <StatusPill status={user.status} />
              {user.plan && user.plan !== "Free" && (
                <PlanPill plan={user.plan} trialEndsAt={user.trialEndsAt} />
              )}
            </div>
          </div>
        </div>

        {/* Actions row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, padding: "10px 12px", borderTop: `1px solid ${T.border}`, background: "#fafafa" }}>
          {user.role === "manager" && (
            <IconBtn icon={Settings} tooltip="Permissions" onClick={openPerms} disabled={loading} variant="iamber" />
          )}
          <IconBtn icon={BadgeCheck} tooltip={user.role === "manager" ? "Remove Ceed Manager" : "Make Ceed Manager"} onClick={handleToggleCeed} disabled={loading} variant={user.role === "manager" ? "iblue" : "default"} />
          <IconBtn icon={Shield} tooltip={user.role === "admin" ? "Remove Admin" : "Make Admin"} onClick={handleRoleToggle} disabled={loading} variant={user.role === "admin" ? "ipurple" : "default"} />
          <IconBtn icon={user.status === "active" ? UserX : UserCheck} tooltip={user.status === "active" ? "Suspend" : "Activate"} onClick={handleStatusToggle} disabled={loading} variant={user.status === "active" ? "ired" : "igreen"} />
          <IconBtn icon={Info} tooltip="View details" onClick={() => onShowDetails(user.id)} disabled={loading} variant="default" />
          <IconBtn icon={Trash2} tooltip={isOwner ? "Delete (transfer)" : "Delete user"} onClick={() => isOwner ? onOpenOwnerDelete(user.id) : setConfirmDel(true)} disabled={loading} variant="ired" />
          <ManagerPermsDialog open={permsOpen} onClose={() => setPermsOpen(false)} user={permsUser} initial={permsData} onSave={savePerms} />
        </div>
      </div>

      <MakeAdminDialog open={showMakeAdmin} onClose={() => setShowMakeAdmin(false)} onConfirm={confirmAdmin} />
      {confirmDel && (
        <ConfirmDialog
          title="Delete User"
          body={<>Email: <strong>{user.email}</strong><br />This action is permanent and cannot be undone.</>}
          confirmLabel="Delete" danger
          onCancel={() => setConfirmDel(false)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ICON BUTTON WITH TOOLTIP
═══════════════════════════════════════════════════════════════════ */
function IconBtn({
  icon: Icon,
  tooltip,
  onClick,
  disabled,
  variant = "default",
}) {
  const [tip, setTip] = useState(false);
  const colorMap = {
    default: T.muted,
    ired: "#dc2626",
    igreen: "#16a34a",
    ipurple: "#7c3aed",
    iamber: T.amberDark,
    iblue: "#1d4ed8",
  };
  return (
    <div style={{ position: "relative" }}>
      <button
        className={`ump-icon-btn ${variant}`}
        onClick={onClick}
        disabled={disabled}
        onMouseEnter={() => setTip(true)}
        onMouseLeave={() => setTip(false)}
        style={{ color: colorMap[variant] || T.muted }}
      >
        <Icon size={15} />
      </button>
      {tip && !disabled && <span className="ump-tip">{tooltip}</span>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PILL COMPONENTS
═══════════════════════════════════════════════════════════════════ */
function RolePill({ role }) {
  const map = {
    admin: {
      bg: "#ede9fe",
      color: "#7c3aed",
      icon: <Shield size={10} />,
      label: "ADMIN",
    },
    manager: {
      bg: "#dbeafe",
      color: "#1d4ed8",
      icon: <BadgeCheck size={10} />,
      label: "CEED MGR",
    },
  };
  const c = map[role] || {
    bg: "#f3f4f6",
    color: "#374151",
    icon: null,
    label: (role || "USER").toUpperCase(),
  };
  return (
    <span className="ump-pill" style={{ background: c.bg, color: c.color }}>
      {c.icon}
      {c.label}
    </span>
  );
}

function AccountTypePill({ type, orgName }) {
  const map = {
    owner: {
      bg: "#fef9c3",
      color: "#854d0e",
      icon: <Crown size={10} />,
      label: "Owner",
    },
    staff: {
      bg: "#dbeafe",
      color: "#1e40af",
      icon: <Briefcase size={10} />,
      label: "Staff",
    },
    member: {
      bg: "#d1fae5",
      color: "#065f46",
      icon: <UsersIcon size={10} />,
      label: "Member",
    },
    individual: {
      bg: "#f3f4f6",
      color: "#374151",
      icon: <UserIcon size={10} />,
      label: "Individual",
    },
  };
  const c = map[type] || map.individual;
  return (
    <span
      className="ump-pill"
      style={{ background: c.bg, color: c.color }}
      title={orgName ? `${c.label} · ${orgName}` : c.label}
    >
      {c.icon}
      {c.label}
    </span>
  );
}

function VerifiedPill({ verified }) {
  return (
    <span
      className="ump-pill"
      style={{
        background: verified ? "#dcfce7" : "#fee2e2",
        color: verified ? "#15803d" : "#b91c1c",
      }}
    >
      <span
        className="ump-pill-dot"
        style={{ background: verified ? T.green : T.red }}
      />
      {verified ? "Verified" : "Unverified"}
    </span>
  );
}

function StatusPill({ status }) {
  const active = status === "active";
  return (
    <span
      className="ump-pill"
      style={{
        background: active ? "#dcfce7" : "#fee2e2",
        color: active ? "#15803d" : "#b91c1c",
      }}
    >
      <span
        className="ump-pill-dot"
        style={{ background: active ? T.green : T.red }}
      />
      {(status || "—").toUpperCase()}
    </span>
  );
}

// ── PlanPill — shows plan name, or "Free Trial" with expiry when on trial ──
function PlanPill({ plan, trialEndsAt }) {
  const isTrial = plan === "Free Trial";

  if (!plan || plan === "Free") {
    return (
      <span style={{ fontFamily: T.fontBody, fontSize: ".82rem", color: T.mutedLight }}>
        Free
      </span>
    );
  }

  if (isTrial) {
    const end = trialEndsAt ? new Date(trialEndsAt) : null;
    const daysLeft = end
      ? Math.max(0, Math.ceil((end - new Date()) / 86_400_000))
      : null;
    const warning = daysLeft !== null && daysLeft <= 1;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <span
          className="ump-pill"
          style={{
            background: warning ? "rgba(239,68,68,.1)" : T.amberBg,
            color:      warning ? "#b91c1c"             : T.amberDark,
            border:     `1px solid ${warning ? "rgba(239,68,68,.25)" : T.amberBorder}`,
            animation:  warning ? "ump-trial-warn 1.8s ease-in-out infinite" : "none",
          }}
        >
          <Clock size={9} style={{ marginRight: 2 }} />
          FREE TRIAL
        </span>
        {end && (
          <span style={{
            fontFamily: T.fontBody, fontSize: ".57rem",
            color: warning ? "#dc2626" : T.muted,
            letterSpacing: ".04em",
          }}>
            {daysLeft === 0
              ? "Expires today"
              : daysLeft === 1
                ? "1 day left"
                : `${daysLeft}d left`}
            {" · "}{end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        )}
      </div>
    );
  }

  return (
    <span style={{ fontFamily: T.fontBody, fontWeight: 600, fontSize: ".82rem", color: "#111827" }}>
      {plan}
    </span>
  );
}

function Pager({ page, total, pageSize, loading, onPage, onPageSize }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const sizes = [10, 25, 50, 100];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [open]);

  const range = useMemo(() => {
    const p = Math.min(pages, 5);
    let s;
    if (pages <= 5) s = 1;
    else if (page <= 3) s = 1;
    else if (page >= pages - 2) s = pages - 4;
    else s = page - 2;
    return Array.from({ length: p }, (_, i) => s + i);
  }, [page, pages]);

  return (
    <div
      className="ump-pager-inner"
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        padding: "14px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span
          style={{ fontFamily: T.fontBody, fontSize: ".82rem", color: T.muted }}
        >
          Showing{" "}
          <strong style={{ color: "#111827" }}>
            {start}–{end}
          </strong>{" "}
          of <strong style={{ color: "#111827" }}>{total}</strong>
        </span>
        <div ref={ref} style={{ position: "relative" }}>
          <button
            onClick={() => setOpen((p) => !p)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              background: "#fff",
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              fontFamily: T.fontBody,
              fontSize: ".65rem",
              letterSpacing: ".08em",
              cursor: "pointer",
              color: T.muted,
            }}
          >
            <Rows size={13} /> {pageSize} rows <ChevronDown size={11} />
          </button>
          {open && (
            <div
              style={{
                position: "absolute",
                bottom: "calc(100% + 6px)",
                left: 0,
                background: "#fff",
                border: `1px solid ${T.border}`,
                borderRadius: 10,
                boxShadow: "0 4px 20px rgba(0,0,0,.1)",
                zIndex: 50,
                overflow: "hidden",
                minWidth: 110,
              }}
            >
              {sizes.map((sz) => (
                <button
                  key={sz}
                  onClick={() => {
                    onPageSize(sz);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    padding: "9px 16px",
                    textAlign: "left",
                    fontFamily: T.fontBody,
                    fontSize: ".65rem",
                    letterSpacing: ".08em",
                    background: sz === pageSize ? T.amberBg : "transparent",
                    color: sz === pageSize ? T.amberDark : T.muted,
                    border: "none",
                    cursor: "pointer",
                    fontWeight: sz === pageSize ? 700 : 400,
                  }}
                >
                  {sz} rows
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="ump-pager-pages" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button
          className="ump-page-btn"
          disabled={page <= 1 || loading}
          onClick={() => onPage(page - 1)}
        >
          ‹
        </button>
        {range.map((n) => (
          <button
            key={n}
            className={`ump-page-btn${n === page ? " pactive" : ""}`}
            onClick={() => onPage(n)}
          >
            {n}
          </button>
        ))}
        <button
          className="ump-page-btn"
          disabled={page >= pages || loading}
          onClick={() => onPage(page + 1)}
        >
          ›
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   USER DETAIL DRAWER
═══════════════════════════════════════════════════════════════════ */
function UserDetailDrawer({ userId, onClose, onDeleted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [delWiz, setDelWiz] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        setLoading(true);
        const d = await getUserDetail(userId);
        if (ok) setData(d);
      } catch (e) {
        toast.error(e?.message || "Failed to load");
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [userId]); // eslint-disable-line

  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const owned = data?.orgsOwned?.[0];
  const member = data?.organizations?.[0];
  const typeLabel =
    {
      owner: "Company Owner",
      staff: "Company Staff",
      member: "Company Member",
      individual: "Individual",
    }[data?.accountType] || "—";

  let hue = 0;
  for (const c of data?.email || "x")
    hue = (hue * 31 + c.charCodeAt(0)) & 0xffff;
  hue = hue % 360;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 60,
          background: "rgba(0,0,0,.45)",
          backdropFilter: "blur(3px)",
        }}
      />
      <aside className="ump-drawer open">
        <div
          style={{
            height: 3,
            background: `linear-gradient(90deg,#111111,#333333)`,
            flexShrink: 0,
          }}
        />

        <div
          style={{
            padding: "18px 24px",
            borderBottom: `1px solid ${T.border}`,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                fontFamily: T.fontBody,
                fontSize: ".57rem",
                letterSpacing: ".04em",
                color: T.amber,
                marginBottom: 4,
              }}
            >
              User Details
            </div>
            <div
              style={{
                fontFamily: T.fontBody,
                fontSize: "1.5rem",
                letterSpacing: ".04em",
                color: T.dark,
              }}
            >
              PROFILE
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 34,
              height: 34,
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              background: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: T.muted,
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 24,
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[80, 60, 40, 60, 40].map((w, i) => (
                <div
                  key={i}
                  className="ump-shimmer"
                  style={{
                    height: 16,
                    width: `${w}%`,
                    animationDelay: `${i * 80}ms`,
                  }}
                />
              ))}
            </div>
          ) : !data ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
              }}
            >
              <span style={{ fontFamily: T.fontBody, color: T.mutedLight }}>
                No data available
              </span>
            </div>
          ) : (
            <>
              {/* Profile hero */}
              <div
                style={{
                  background: "#fff",
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: 56,
                    background: `linear-gradient(135deg, hsl(${hue},55%,30%), hsl(${(hue + 50) % 360},65%,40%))`,
                  }}
                />
                <div style={{ padding: "0 20px 20px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      marginTop: -28,
                    }}
                  >
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, hsl(${hue},60%,45%), hsl(${(hue + 40) % 360},70%,55%))`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: T.fontBody,
                        fontSize: "1.6rem",
                        color: "#fff",
                        border: "3px solid #fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,.1)",
                      }}
                    >
                      {(data.name || data.email || "U")[0].toUpperCase()}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                      }}
                    >
                      <StatusPill status={data.status} />
                      <VerifiedPill verified={data.verified} />
                      {data.effectivePlan?.label && (
                        <PlanPill
                          plan={data.effectivePlan.label}
                          trialEndsAt={data.effectivePlan.scope === "trial" ? data.effectivePlan.trialEndsAt : null}
                        />
                      )}
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div
                      style={{
                        fontFamily: T.fontBody,
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: "#111827",
                      }}
                    >
                      {data.name || "Unnamed User"}
                    </div>
                    <div
                      style={{
                        fontFamily: T.fontBody,
                        fontSize: ".68rem",
                        color: T.muted,
                        marginTop: 2,
                      }}
                    >
                      {data.email}
                    </div>
                    <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                      <RolePill role={data.role} />
                      <AccountTypePill type={data.accountType} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Info */}
              <DrawerSection title="Account Info">
                <DrawerRow label="Phone" value={data.phone} />
                <DrawerRow
                  label="Professional Role"
                  value={data.professionalRole}
                />
                <DrawerRow label="Account Type" value={typeLabel} />
                {data.effectivePlan?.scope === "org" && (
                  <DrawerRow
                    label="Company"
                    value={data.effectivePlan.orgName}
                  />
                )}
                {/* Free trial details */}
                {data.effectivePlan?.scope === "trial" && (() => {
                  const end = data.effectivePlan.trialEndsAt
                    ? new Date(data.effectivePlan.trialEndsAt)
                    : null;
                  const start = data.effectivePlan.trialStartedAt
                    ? new Date(data.effectivePlan.trialStartedAt)
                    : null;
                  const daysLeft = end
                    ? Math.max(0, Math.ceil((end - new Date()) / 86_400_000))
                    : null;
                  const warning = daysLeft !== null && daysLeft <= 1;
                  return (
                    <>
                      <DrawerRow
                        label="Trial Started"
                        value={start ? start.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—"}
                      />
                      <DrawerRow
                        label="Trial Expires"
                        value={end ? end.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—"}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span style={{ fontFamily: T.fontBody, fontSize: ".58rem", letterSpacing: ".04em", color: T.mutedLight }}>
                          Time Remaining
                        </span>
                        <span style={{
                          fontFamily: T.fontBody,
                          fontSize: ".875rem",
                          fontWeight: 600,
                          color: warning ? "#b91c1c" : T.amberDark,
                        }}>
                          {daysLeft === null
                            ? "—"
                            : daysLeft === 0
                              ? "Expires today"
                              : daysLeft === 1
                                ? "1 day remaining"
                                : `${daysLeft} days remaining`}
                        </span>
                      </div>
                      {(data.effectivePlan.trialFeatures || []).length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <span style={{ fontFamily: T.fontBody, fontSize: ".58rem", letterSpacing: ".04em", color: T.mutedLight }}>
                            Trial Features
                          </span>
                          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                            {(data.effectivePlan.trialFeatures || []).map((f) => (
                              <span key={f} style={{
                                fontFamily: T.fontBody, fontSize: ".58rem",
                                padding: "2px 7px", borderRadius: 4,
                                background: "rgba(16,185,129,.1)", color: "#065f46",
                                border: "1px solid rgba(16,185,129,.2)",
                                letterSpacing: ".04em",
                              }}>
                                {f.replace("feat.", "")}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </DrawerSection>

              {/* Owned company */}
              {data.accountType === "owner" && owned && (
                <DrawerSection title="Company" accent={T.amber}>
                  <DrawerRow label="Name" value={owned.name} />
                  <DrawerRow label="Address" value={owned.address} />
                  <DrawerRow label="Website" value={owned.website} />
                  <DrawerRow label="Contact Email" value={owned.contactEmail} />
                  <DrawerRow label="Contact Phone" value={owned.contactPhone} />
                  {owned.subscriptions?.length > 0 && (
                    <div
                      style={{
                        marginTop: 10,
                        paddingTop: 10,
                        borderTop: `1px solid ${T.border}`,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: T.fontBody,
                          fontSize: ".57rem",
                          letterSpacing: ".04em",
                          color: T.muted,
                          marginBottom: 6,
                        }}
                      >
                        Subscriptions
                      </div>
                      {owned.subscriptions.map((s) => (
                        <div
                          key={s.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontFamily: T.fontBody,
                            fontSize: ".82rem",
                            padding: "3px 0",
                          }}
                        >
                          <span style={{ color: "#111827" }}>{s.planName}</span>
                          <span style={{ color: T.muted }}>
                            {s.currentPeriodEnd
                              ? new Date(
                                  s.currentPeriodEnd,
                                ).toLocaleDateString()
                              : "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </DrawerSection>
              )}

              {/* Member of company */}
              {(data.accountType === "staff" ||
                data.accountType === "member") &&
                member && (
                  <DrawerSection title="Company" accent={T.green}>
                    <DrawerRow label="Company" value={member.name} />
                    <DrawerRow
                      label="Role"
                      value={member.companyRole || data.companyRole}
                    />
                    <DrawerRow label="Owner Email" value={member.ownerEmail} />
                  </DrawerSection>
                )}
            </>
          )}
        </div>

        <div
          style={{
            padding: "14px 24px",
            borderTop: `1px solid ${T.border}`,
            background: "#fafafa",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 18px",
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              background: "#fff",
              fontFamily: T.fontBody,
              fontSize: ".82rem",
              fontWeight: 600,
              cursor: "pointer",
              color: T.muted,
            }}
          >
            Close
          </button>
          {data?.accountType === "owner" ? (
            <button
              onClick={() => setDelWiz(true)}
              style={{
                padding: "8px 18px",
                border: "none",
                borderRadius: 8,
                background: T.dark,
                color: "#fff",
                fontFamily: T.fontBody,
                fontSize: ".82rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Delete Owner…
            </button>
          ) : (
            <button
              onClick={async () => {
                try {
                  await deleteUser(userId);
                  toast.success("Deleted");
                  onDeleted();
                } catch (e) {
                  toast.error(e?.message || "Failed");
                }
              }}
              style={{
                padding: "8px 18px",
                border: "none",
                borderRadius: 8,
                background: T.red,
                color: "#fff",
                fontFamily: T.fontBody,
                fontSize: ".82rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Delete User
            </button>
          )}
        </div>

        <DeleteOwnerWizard
          ownerId={userId}
          open={delWiz}
          onClose={() => setDelWiz(false)}
          onDone={() => {
            setDelWiz(false);
            onDeleted();
          }}
        />
      </aside>
    </>
  );
}

/* ── Drawer Section + Row ───────────────────────────────────────── */
function DrawerSection({ title, children, accent }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 20px",
          borderBottom: `1px solid ${T.border}`,
          background: "#fafafa",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {accent && (
          <div
            style={{
              width: 3,
              height: 14,
              borderRadius: 2,
              background: accent,
            }}
          />
        )}
        <span
          style={{
            fontFamily: T.fontBody,
            fontSize: ".95rem",
            letterSpacing: ".06em",
            color: T.dark,
          }}
        >
          {title.toUpperCase()}
        </span>
      </div>
      <div
        style={{
          padding: "14px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {children}
      </div>
    </div>
  );
}
function DrawerRow({ label, value }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span
        style={{
          fontFamily: T.fontBody,
          fontSize: ".58rem",
          letterSpacing: ".04em",
          color: T.mutedLight,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: T.fontBody,
          fontSize: ".875rem",
          color: value ? "#111827" : T.mutedLight,
          fontWeight: value ? 500 : 400,
        }}
      >
        {value || "—"}
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   DIALOGS
═══════════════════════════════════════════════════════════════════ */
function ConfirmDialog({
  title,
  body,
  confirmLabel,
  danger,
  onCancel,
  onConfirm,
}) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 70 }}>
      <div
        onClick={onCancel}
        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.4)" }}
      />
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%,-50%)",
          width: "min(92vw,460px)",
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 8px 48px rgba(0,0,0,.18)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <div
            style={{
              fontFamily: T.fontBody,
              fontSize: "1.2rem",
              letterSpacing: ".04em",
              color: T.dark,
            }}
          >
            {title.toUpperCase()}
          </div>
        </div>
        <div
          style={{
            padding: "18px 24px",
            fontFamily: T.fontBody,
            fontSize: ".875rem",
            color: "#374151",
            lineHeight: 1.6,
          }}
        >
          {body}
        </div>
        <div
          style={{
            padding: "14px 24px",
            background: "#fafafa",
            borderTop: `1px solid ${T.border}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <button
            onClick={onCancel}
            style={{
              padding: "8px 18px",
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              background: "#fff",
              fontFamily: T.fontBody,
              fontSize: ".82rem",
              fontWeight: 600,
              cursor: "pointer",
              color: T.muted,
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "8px 18px",
              border: "none",
              borderRadius: 8,
              background: danger ? T.red : T.dark,
              color: "#fff",
              fontFamily: T.fontBody,
              fontSize: ".82rem",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function MakeAdminDialog({ open, onClose, onConfirm }) {
  const [step, setStep] = useState(1);
  const [text, setText] = useState("");
  useEffect(() => {
    if (!open) {
      setStep(1);
      setText("");
    }
  }, [open]);
  if (!open) return null;
  const ok = step === 1 || text.trim() === "MAKE ADMIN";
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 70 }}>
      <div
        onClick={onClose}
        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.4)" }}
      />
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%,-50%)",
          width: "min(92vw,500px)",
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 8px 48px rgba(0,0,0,.18)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <div
            style={{
              fontFamily: T.fontBody,
              fontSize: "1.2rem",
              letterSpacing: ".04em",
              color: T.dark,
            }}
          >
            MAKE USER ADMIN
          </div>
          <div
            style={{
              fontFamily: T.fontBody,
              fontSize: ".82rem",
              color: T.muted,
              marginTop: 4,
            }}
          >
            This grants full platform access.
          </div>
        </div>
        <div style={{ padding: "20px 24px" }}>
          {step === 1 ? (
            <>
              <p
                style={{
                  fontFamily: T.fontBody,
                  fontWeight: 600,
                  fontSize: ".875rem",
                  color: "#111827",
                  marginBottom: 10,
                }}
              >
                Are you sure?
              </p>
              <ul
                style={{
                  fontFamily: T.fontBody,
                  fontSize: ".82rem",
                  color: "#374151",
                  paddingLeft: 20,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <li>Admins can manage all billing and plans</li>
                <li>Admins can modify or delete any user</li>
                <li>Admins can make irreversible changes</li>
              </ul>
            </>
          ) : (
            <>
              <p
                style={{
                  fontFamily: T.fontBody,
                  fontWeight: 600,
                  fontSize: ".875rem",
                  color: "#111827",
                  marginBottom: 10,
                }}
              >
                Type{" "}
                <code
                  style={{
                    background: "#f3f4f6",
                    padding: "2px 6px",
                    borderRadius: 4,
                    fontFamily: T.fontBody,
                    fontSize: ".82rem",
                  }}
                >
                  MAKE ADMIN
                </code>{" "}
                to confirm.
              </p>
              <input
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="MAKE ADMIN"
                className="ump-input"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "10px 14px",
                }}
              />
            </>
          )}
        </div>
        <div
          style={{
            padding: "14px 24px",
            background: "#fafafa",
            borderTop: `1px solid ${T.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 18px",
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              background: "#fff",
              fontFamily: T.fontBody,
              fontSize: ".82rem",
              fontWeight: 600,
              cursor: "pointer",
              color: T.muted,
            }}
          >
            Cancel
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            {step === 2 && (
              <button
                onClick={() => setStep(1)}
                style={{
                  padding: "8px 18px",
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  background: "#fff",
                  fontFamily: T.fontBody,
                  fontSize: ".82rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  color: T.muted,
                }}
              >
                Back
              </button>
            )}
            {step === 1 ? (
              <button
                onClick={() => setStep(2)}
                style={{
                  padding: "8px 18px",
                  border: "none",
                  borderRadius: 8,
                  background: T.dark,
                  color: "#fff",
                  fontFamily: T.fontBody,
                  fontSize: ".82rem",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Continue
              </button>
            ) : (
              <button
                onClick={onConfirm}
                disabled={!ok}
                style={{
                  padding: "8px 18px",
                  border: "none",
                  borderRadius: 8,
                  background: ok ? T.red : "#fca5a5",
                  color: "#fff",
                  fontFamily: T.fontBody,
                  fontSize: ".82rem",
                  fontWeight: 700,
                  cursor: ok ? "pointer" : "not-allowed",
                }}
              >
                Confirm
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}